import {cardClass} from '@kapitalist/contract/ruleset'
import type {
  TBirthdayCardData,
  TCardData,
  TCashCardData,
  TDealBreakerCardData,
  TDebtCollectorCardData,
  TDirectionalRentCardData,
  TDoubleTheRentCardData,
  TDualPropertyCardData,
  TForcedDealCardData,
  TLastRentThisVisit,
  TLiquidatableCardData,
  TMajorActionBirthdayData,
  TMajorActionChangeNeighborhoodData,
  TMajorActionData,
  TMajorActionDataThatAllowsMinorAction,
  TMajorActionDealBreakerData,
  TMajorActionDebtCollectorData,
  TMajorActionDirectionalRentData,
  TMajorActionDiscardData,
  TMajorActionDoubleTheRentData,
  TMajorActionForcedDealData,
  TMajorActionPassGoData,
  TMajorActionPlayAsCashData,
  TMajorActionPlayToNeighborhoodData,
  TMajorActionResidenceToCashData,
  TMajorActionSlyDealData,
  TMajorActionUniversalRentData,
  TMinorActionData,
  TMinorActionDataAcceptDealBreaker,
  TMinorActionDataAcceptForcedDeal,
  TMinorActionDataAcceptSlyDeal,
  TMinorActionDataAcquiesce,
  TMinorActionDataJustSayNo,
  TMinorActionDataPayOther,
  TMinorActionDataPayRent,
  TMinorActionPromptData,
  TMinorActionPromptDataDealBreaker,
  TMinorActionPromptDataDebtCollectorBirthday,
  TMinorActionPromptDataForcedDeal,
  TMinorActionPromptDataJustSayNo,
  TMinorActionPromptDataPayRent,
  TMinorActionPromptDataSlyDeal,
  TPassGoCardData,
  TPortfolioNeighborhoodData,
  TPropertyCardData,
  TPropertyWildCardData,
  TPropertyWildCardPortfolioNeighborhoodData,
  TResidenceCardData,
  TSlyDealCardData,
  TStandardPortfolioNeighborhoodData,
  TStandardPropertyCardData,
  TUniversalRentCardData,
  TJustSayNoCardData,
} from '@kapitalist/contract/types'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import type {TNonRentActionCardData, TPlayableCardData} from '~/game/types'
import {
  BirthdayCard,
  DealBreakerCard,
  DebtCollectorCard,
  DoubleTheRentCard,
  ForcedDealCard,
  JustSayNoCard,
  PassGoCard,
  SlyDealCard,
} from '~/models/game/Cards/ActionCard'
import CashCard from '~/models/game/Cards/CashCard'
import DualPropertyCard from '~/models/game/Cards/DualPropertyCard'
import PropertyWildCard from '~/models/game/Cards/PropertyWildCard'
import {DirectionalRentCard, UniversalRentCard} from '~/models/game/Cards/RentCard'
import ResidenceCard from '~/models/game/Cards/ResidenceCard'
import StandardPropertyCard from '~/models/game/Cards/StandardPropertyCard'
import type GamePlayer from '~/models/game/GamePlayer'
import type MajorAction from '~/models/game/MajorAction'
import MajorActionBirthday from '~/models/game/MajorActions/MajorActionBirthday'
import MajorActionChangeNeighborhood from '~/models/game/MajorActions/MajorActionChangeNeighborhood'
import MajorActionDealBreaker from '~/models/game/MajorActions/MajorActionDealBreaker'
import MajorActionDebtCollector from '~/models/game/MajorActions/MajorActionDebtCollector'
import MajorActionDirectionalRent from '~/models/game/MajorActions/MajorActionDirectionalRent'
import MajorActionDiscard from '~/models/game/MajorActions/MajorActionDiscard'
import MajorActionDoubleTheRent from '~/models/game/MajorActions/MajorActionDoubleTheRent'
import MajorActionForcedDeal from '~/models/game/MajorActions/MajorActionForcedDeal'
import MajorActionPassGo from '~/models/game/MajorActions/MajorActionPassGo'
import MajorActionPlayAsCash from '~/models/game/MajorActions/MajorActionPlayAsCash'
import MajorActionPlayToNeighborhood from '~/models/game/MajorActions/MajorActionPlayToNeighborhood'
import type MajorActionRentBase from '~/models/game/MajorActions/MajorActionRentBase'
import MajorActionResidenceToCash from '~/models/game/MajorActions/MajorActionResidenceToCash'
import MajorActionSlyDeal from '~/models/game/MajorActions/MajorActionSlyDeal'
import MajorActionUniversalRent from '~/models/game/MajorActions/MajorActionUniversalRent'
import MinorActionPromptDealBreaker from '~/models/game/MinorActionPrompts/MinorActionPromptDealBreaker'
import MinorActionPromptForcedDeal from '~/models/game/MinorActionPrompts/MinorActionPromptForcedDeal'
import MinorActionPromptJustSayNo from '~/models/game/MinorActionPrompts/MinorActionPromptJustSayNo'
import MinorActionPromptPayOther from '~/models/game/MinorActionPrompts/MinorActionPromptPayOther'
import MinorActionPromptPayRent from '~/models/game/MinorActionPrompts/MinorActionPromptPayRent'
import MinorActionPromptSlyDeal from '~/models/game/MinorActionPrompts/MinorActionPromptSlyDeal'
import MinorActionAcceptDealBreaker from '~/models/game/MinorActions/MinorActionAcceptDealBreaker'
import MinorActionAcceptForcedDeal from '~/models/game/MinorActions/MinorActionAcceptForcedDeal'
import MinorActionAcceptSlyDeal from '~/models/game/MinorActions/MinorActionAcceptSlyDeal'
import MinorActionAcquiesce from '~/models/game/MinorActions/MinorActionAcquiesce'
import MinorActionJustSayNo from '~/models/game/MinorActions/MinorActionJustSayNo'
import MinorActionPayOther from '~/models/game/MinorActions/MinorActionPayOther'
import MinorActionPayRent from '~/models/game/MinorActions/MinorActionPayRent'
import type Portfolio from '~/models/game/Portfolio'
import type PortfolioNeighborhood from '~/models/game/PortfolioNeighborhood'
import StandardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/StandardPortfolioNeighborhood'
import WildCardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/WildCardPortfolioNeighborhood'
import type {
  AnyActionCard,
  AnyCard,
  AnyLiquidatableCard,
  AnyMinorAction,
  AnyMinorActionPrompt,
  AnyPlayableCard,
  AnyPropertyCard,
  MajorActionAllowsMinorActions,
} from '~/models/game/types'

function cardFactory(c: TNonRentActionCardData): AnyActionCard
function cardFactory(c: TCashCardData): CashCard
function cardFactory(c: TDualPropertyCardData): DualPropertyCard
function cardFactory(c: TPropertyWildCardData): PropertyWildCard
function cardFactory(c: TStandardPropertyCardData): StandardPropertyCard
function cardFactory(c: TResidenceCardData): ResidenceCard
function cardFactory(c: TPlayableCardData): AnyPlayableCard
function cardFactory(c: TDirectionalRentCardData): DirectionalRentCard
function cardFactory(c: TUniversalRentCardData): UniversalRentCard
function cardFactory(c: TLiquidatableCardData): AnyLiquidatableCard
function cardFactory(
  c: TDualPropertyCardData | TPropertyWildCardData
): DualPropertyCard | PropertyWildCard
function cardFactory(
  c: TDirectionalRentCardData | TUniversalRentCardData
): UniversalRentCard | DirectionalRentCard
function cardFactory(
  c: TLiquidatableCardData | TPropertyCardData
): AnyLiquidatableCard | AnyPropertyCard
function cardFactory(c: TCardData): AnyCard
function cardFactory(c: TCardData): AnyCard {
  const ct = cardClass[c.type]
  // the `as` casts below are needed because TCardData isn't a discriminated union,
  // so neither the `cardClass` lookup nor `c.type` narrows `c` to a specific data type
  switch (ct) {
    case 'action':
      return actionCardFactory(c as TNonRentActionCardData)
    case 'cash':
      return new CashCard(c as TCashCardData)
    case 'dualProperty':
      return new DualPropertyCard(c as TDualPropertyCardData)
    case 'propertyWildCard':
      return new PropertyWildCard(c as TPropertyWildCardData)
    case 'standardProperty':
      return new StandardPropertyCard(c as TStandardPropertyCardData)
    case 'rent':
      if (c.type === 'directionalRent') {
        return new DirectionalRentCard(c as TDirectionalRentCardData)
      } else {
        return new UniversalRentCard(c as TUniversalRentCardData)
      }
    case 'residence':
      return new ResidenceCard(c as TResidenceCardData)
    default:
      return unreachableCase(ct)
  }
}

function actionCardFactory(c: TBirthdayCardData): BirthdayCard
function actionCardFactory(c: TDealBreakerCardData): DealBreakerCard
function actionCardFactory(c: TDebtCollectorCardData): DebtCollectorCard
function actionCardFactory(c: TDoubleTheRentCardData): DoubleTheRentCard
function actionCardFactory(c: TForcedDealCardData): ForcedDealCard
function actionCardFactory(c: TJustSayNoCardData): JustSayNoCard
function actionCardFactory(c: TPassGoCardData): PassGoCard
function actionCardFactory(c: TSlyDealCardData): SlyDealCard
function actionCardFactory(c: TNonRentActionCardData): AnyActionCard
function actionCardFactory(c: TNonRentActionCardData): AnyActionCard {
  switch (c.type) {
    case 'birthday':
      return new BirthdayCard(c as TBirthdayCardData)
    case 'dealBreaker':
      return new DealBreakerCard(c as TDealBreakerCardData)
    case 'debtCollector':
      return new DebtCollectorCard(c as TDebtCollectorCardData)
    case 'doubleTheRent':
      return new DoubleTheRentCard(c as TDoubleTheRentCardData)
    case 'forcedDeal':
      return new ForcedDealCard(c as TForcedDealCardData)
    case 'justSayNo':
      return new JustSayNoCard(c as TJustSayNoCardData)
    case 'passGo':
      return new PassGoCard(c as TPassGoCardData)
    case 'slyDeal':
      return new SlyDealCard(c as TSlyDealCardData)
    default:
      return unreachableCase(c.type)
  }
}

function propertyCardFactory(c: TDualPropertyCardData): DualPropertyCard
function propertyCardFactory(c: TPropertyWildCardData): PropertyWildCard
function propertyCardFactory(c: TStandardPropertyCardData): StandardPropertyCard
function propertyCardFactory(
  c: TStandardPropertyCardData | TDualPropertyCardData | TPropertyWildCardData
): StandardPropertyCard | DualPropertyCard | PropertyWildCard
function propertyCardFactory(c: TPropertyCardData): AnyPropertyCard {
  switch (c.type) {
    case 'wildUtilityAndRailroad':
    case 'wildRedAndYellow':
    case 'wildPinkAndOrange':
    case 'wildLightBlueAndRailroad':
    case 'wildLightBlueAndBrown':
    case 'wildGreenAndRailroad':
    case 'wildDarkBlueAndGreen':
      return new DualPropertyCard(c)
    case 'propertyWildCard':
      return new PropertyWildCard(c)
    default:
      return new StandardPropertyCard(c)
  }
}

function neighborhoodCardFactory(
  c: TPropertyCardData | TResidenceCardData
): AnyPropertyCard | ResidenceCard {
  switch (c.type) {
    case 'house':
    case 'hotel':
      return new ResidenceCard(c)
    default:
      return propertyCardFactory(c)
  }
}

function portfolioNeighborhoodFactory(
  d: TStandardPortfolioNeighborhoodData
): StandardPortfolioNeighborhood
function portfolioNeighborhoodFactory(
  d: TPropertyWildCardPortfolioNeighborhoodData
): WildCardPortfolioNeighborhood
function portfolioNeighborhoodFactory(
  d: TPortfolioNeighborhoodData
): PortfolioNeighborhood
function portfolioNeighborhoodFactory(
  d: TPortfolioNeighborhoodData
): PortfolioNeighborhood {
  switch (d.neighborhood) {
    case 'alone':
      return new WildCardPortfolioNeighborhood(d)
    default:
      return new StandardPortfolioNeighborhood(d)
  }
}

// DoubleTheRent makes things complicated. I could store lastRentThisVisit inside Portfolio
// to avoid this, since that is the only card that needs more than the current Portfolio to figure itself out,
// but that didn't seem right from a data organization perspective. We'll see how this plays out.
type TMajorActionFactoryExtraData = {
  lastRentThisVisit?: TLastRentThisVisit
}

function majorActionFactory(
  ad: TMajorActionPassGoData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionPassGo
function majorActionFactory(
  ad: TMajorActionDirectionalRentData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionDirectionalRent
function majorActionFactory(
  ad: TMajorActionUniversalRentData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionUniversalRent
function majorActionFactory(
  ad: TMajorActionDoubleTheRentData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionDoubleTheRent
function majorActionFactory(
  ad: TMajorActionSlyDealData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionSlyDeal
function majorActionFactory(
  ad: TMajorActionForcedDealData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionForcedDeal
function majorActionFactory(
  ad: TMajorActionDealBreakerData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionDealBreaker
function majorActionFactory(
  ad: TMajorActionDebtCollectorData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionDebtCollector
function majorActionFactory(
  ad: TMajorActionBirthdayData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionBirthday
function majorActionFactory(
  ad: TMajorActionPlayAsCashData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionPlayAsCash
function majorActionFactory(
  ad: TMajorActionPlayToNeighborhoodData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionPlayToNeighborhood
function majorActionFactory(
  ad: TMajorActionDiscardData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionDiscard
function majorActionFactory(
  ad: TMajorActionChangeNeighborhoodData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionChangeNeighborhood
function majorActionFactory(
  ad: TMajorActionResidenceToCashData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionResidenceToCash
function majorActionFactory(
  ad: TMajorActionDataThatAllowsMinorAction,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorActionAllowsMinorActions
function majorActionFactory(
  ad: TMajorActionData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorAction
function majorActionFactory(
  ad: TMajorActionData,
  portfolio: Portfolio,
  extraData: TMajorActionFactoryExtraData
): MajorAction {
  switch (ad.type) {
    case 'birthday':
      return new MajorActionBirthday(ad)
    case 'dealBreaker':
      return new MajorActionDealBreaker(ad)
    case 'debtCollector':
      return new MajorActionDebtCollector(ad)
    case 'doubleTheRent':
      if (!extraData.lastRentThisVisit) {
        throw new Error(
          'lastRentThisVisit is missing when creating a MajorActionDoubleTheRent in majorActionFactory'
        )
      }
      return new MajorActionDoubleTheRent(ad, portfolio, extraData.lastRentThisVisit)
    case 'forcedDeal':
      return new MajorActionForcedDeal(ad)
    case 'passGo':
      return new MajorActionPassGo(ad)
    case 'directionalRent':
      return new MajorActionDirectionalRent(ad, portfolio)
    case 'rentDarkBlueGreen':
    case 'rentLightBlueBrown':
    case 'rentPinkOrange':
    case 'rentRedYellow':
    case 'rentUtilityRailroad':
      return new MajorActionUniversalRent(ad, portfolio)
    case 'slyDeal':
      return new MajorActionSlyDeal(ad)
    case 'playAsCash':
      return new MajorActionPlayAsCash(ad)
    case 'playToNeighborhood':
      return new MajorActionPlayToNeighborhood(ad)
    case 'discard':
      return new MajorActionDiscard(ad)
    case 'changeNeighborhood':
      return new MajorActionChangeNeighborhood(ad)
    case 'residenceToCash':
      return new MajorActionResidenceToCash(ad)
    default:
      return unreachableCase(ad)
  }
}

function minorActionFactory(ad: TMinorActionDataPayRent): MinorActionPayRent
function minorActionFactory(
  ad: TMinorActionDataAcceptForcedDeal
): MinorActionAcceptForcedDeal
function minorActionFactory(
  ad: TMinorActionDataAcceptSlyDeal
): MinorActionAcceptSlyDeal
function minorActionFactory(
  ad: TMinorActionDataAcceptDealBreaker
): MinorActionAcceptDealBreaker
function minorActionFactory(ad: TMinorActionDataPayOther): MinorActionPayOther
function minorActionFactory(ad: TMinorActionDataJustSayNo): MinorActionJustSayNo
function minorActionFactory(ad: TMinorActionDataAcquiesce): MinorActionAcquiesce
function minorActionFactory(ad: TMinorActionData): AnyMinorAction
function minorActionFactory(ad: TMinorActionData): AnyMinorAction {
  switch (ad.type) {
    case 'birthday':
    case 'debtCollector':
      return new MinorActionPayOther(ad)
    case 'dealBreaker':
      return new MinorActionAcceptDealBreaker(ad)
    case 'forcedDeal':
      return new MinorActionAcceptForcedDeal(ad)
    case 'doubleTheRent':
    case 'directionalRent':
    case 'universalRent':
      return new MinorActionPayRent(ad)
    case 'slyDeal':
      return new MinorActionAcceptSlyDeal(ad)
    case 'justSayNo':
      return new MinorActionJustSayNo(ad)
    case 'acquiesce':
      return new MinorActionAcquiesce(ad)
    default:
      return unreachableCase(ad)
  }
}

type TMinorActionPromptFactoryExtraData = {
  majorActor: GamePlayer
  minorActor: GamePlayer
}

function minorActionPromptFactory(
  ad: TMinorActionPromptDataPayRent,
  majorAction: MajorActionRentBase,
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptPayRent
function minorActionPromptFactory(
  ad: TMinorActionPromptDataForcedDeal,
  majorAction: MajorActionForcedDeal,
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptForcedDeal
function minorActionPromptFactory(
  ad: TMinorActionPromptDataSlyDeal,
  majorAction: MajorActionSlyDeal,
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptSlyDeal
function minorActionPromptFactory(
  ad: TMinorActionPromptDataDealBreaker,
  majorAction: MajorActionDealBreaker,
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptDealBreaker
function minorActionPromptFactory(
  ad: TMinorActionPromptDataDebtCollectorBirthday,
  majorAction: MajorActionDebtCollector | MajorActionBirthday,
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptPayOther
function minorActionPromptFactory(
  ad: TMinorActionPromptDataJustSayNo,
  majorAction: MajorAction, // any major action of yours was JSN'd, you can JSN back
  extraData: TMinorActionPromptFactoryExtraData
): MinorActionPromptJustSayNo
function minorActionPromptFactory(
  ad: TMinorActionPromptData,
  majorAction: MajorAction,
  extraData: TMinorActionPromptFactoryExtraData
): AnyMinorActionPrompt
function minorActionPromptFactory(
  ad: TMinorActionPromptData,
  majorAction: MajorAction,
  extraData: TMinorActionPromptFactoryExtraData
): AnyMinorActionPrompt {
  switch (ad.type) {
    case 'birthday':
    case 'debtCollector':
      return new MinorActionPromptPayOther(
        ad,
        extraData.majorActor,
        extraData.minorActor
      )
    case 'dealBreaker':
      return new MinorActionPromptDealBreaker(
        ad,
        extraData.majorActor,
        extraData.minorActor
      )
    case 'forcedDeal':
      return new MinorActionPromptForcedDeal(
        ad,
        extraData.majorActor,
        extraData.minorActor
      )
    case 'doubleTheRent':
    case 'directionalRent':
    case 'universalRent':
      return new MinorActionPromptPayRent(
        ad,
        // NB: `majorAction` can't be narrowed from a check on `ad`
        majorAction as MajorActionRentBase,
        extraData.majorActor,
        extraData.minorActor
      )
    case 'slyDeal':
      return new MinorActionPromptSlyDeal(
        ad,
        extraData.majorActor,
        extraData.minorActor
      )
    case 'justSayNo':
      return new MinorActionPromptJustSayNo(
        ad,
        extraData.majorActor,
        extraData.minorActor
      )
    default:
      return unreachableCase(ad)
  }
}

export {
  cardFactory,
  propertyCardFactory,
  portfolioNeighborhoodFactory,
  neighborhoodCardFactory,
  majorActionFactory,
  minorActionFactory,
  minorActionPromptFactory,
}
